import { useMemo } from 'react';

import { Button, Flex, List } from '@chakra-ui/react';

import { TileListing } from '@nocowanie/noc-api';

import { IconsLinear } from '@nocowanie/icons/linear';
import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { DistanceFrom } from '@app/components/atoms/distance-from';
import { AccommodationTileLocationProps } from '@app/components/molecules/accommodation-tile';
import { API_PARTIALS } from '@app/consts';
import { DistanceTargetType, LocationTypeEnum } from '@app/enums';
import { buildEsiInclude } from '@app/helpers/common.helpers';
import { formatDistance } from '@app/helpers/distance.helpers';

const MAX_DISPLAY_BEACH_DISTANCE_METERS = 3000;

export const AccommodationTileLocation = ({
    accommodation,
    searchCriteria = {},
    mapUrl,
    beachDistanceMeters,
    t,
    isESI,
    cityLocation,
    varietyGenitive,
}: AccommodationTileLocationProps) => {
    const { id, listingPlacement, name, settlementName, isOTA, coordinates } = accommodation;

    const handleShowOnMap = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        // change to useRouter once accommodation-tiles are fully on SSR
        window.location.href = mapUrl;
    };

    const targetId = useMemo(() => {
        if (searchCriteria.poiId) {
            return `NOC_${searchCriteria.poiId}`;
        }

        if (searchCriteria.locationType === LocationTypeEnum.City) {
            return searchCriteria.geoId;
        }

        return null;
    }, [searchCriteria.geoId, searchCriteria.locationType, searchCriteria.poiId]);

    const showBeachDistance = useMemo(
        () => beachDistanceMeters && beachDistanceMeters <= MAX_DISPLAY_BEACH_DISTANCE_METERS,
        [beachDistanceMeters],
    );

    const renderDistanceItem = () => {
        if (!targetId) return null;

        if (isESI) {
            return (
                <List.Item
                    hideBelow={showBeachDistance ? 'md' : undefined}
                    dangerouslySetInnerHTML={{
                        __html: buildEsiInclude(
                            searchCriteria.poiId
                                ? API_PARTIALS.distance.poi
                                : API_PARTIALS.distance.cityCenter,
                            {
                                accommodationId: id,
                                targetId,
                                ...(!searchCriteria.poiId &&
                                    listingPlacement === TileListing.placement.NEARBY && {
                                        isNearby: true,
                                    }),
                            },
                        ),
                    }}
                />
            );
        }

        if (cityLocation && coordinates) {
            return (
                <List.Item hideBelow={showBeachDistance ? 'md' : undefined}>
                    <DistanceFrom
                        accommodationCoords={{
                            lat: coordinates.lat,
                            lon: coordinates.lon,
                        }}
                        targetCoords={cityLocation}
                        targetType={DistanceTargetType.CityCenter}
                        targetName={varietyGenitive}
                        isNearby={listingPlacement === TileListing.placement.NEARBY}
                    />
                </List.Item>
            );
        }

        return null;
    };

    return (
        <Flex>
            <IconsLinear.Location
                mr={1.5}
                mt={'-2px'}
                fontSize={'sm'}
                css={{ '--icon-stroke': 1 }}
            />
            <List.Root
                fontSize={'xs'}
                data-testid={'acc-tile-location'}
                variant={'separated'}
                lineHeight={1}
                css={{
                    '--list-marker-color': '{colors.gray.500}',
                }}
            >
                <List.Item>{settlementName}</List.Item>
                {isOTA && (
                    <>
                        <List.Item hideBelow={'md'}>
                            <Button
                                type={'button'}
                                aria-label={`${t('showOnMap')} ${name}`}
                                colorPalette={'secondary'}
                                zIndex={'overLinkOverlay'}
                                variant={'link'}
                                size={'2xs'}
                                data-show-on-map-url={mapUrl}
                                onClick={handleShowOnMap}
                                my={-0.5}
                            >
                                {t('showOnMap')}
                            </Button>
                        </List.Item>
                        {renderDistanceItem()}
                    </>
                )}
                {beachDistanceMeters && showBeachDistance ? (
                    <List.Item color={'gray.500'}>
                        <IconsNocowanie.Sea mr={1} fontSize={'sm'} css={{ '--icon-stroke': 1 }} />
                        {t('beachDistance', { distance: formatDistance(beachDistanceMeters) })}
                    </List.Item>
                ) : null}
            </List.Root>
        </Flex>
    );
};
